import type { Interval, LocationConfig, ThemeName } from './types';

const THEMES: { name: ThemeName; label: string; bg: string; }[] = [
  { name: 'navy',   label: 'Navy',   bg: '#0F2C44' },
  { name: 'dark',   label: 'Black',  bg: '#111111' },
  { name: 'green',  label: 'Green',  bg: '#2D824B' },
  { name: 'orange', label: 'Orange', bg: '#c25525' }
];

type Draft = {
  name: string;
  sub: string;
  theme: ThemeName;
  sound: boolean;
  loop: boolean;
  intervals: Interval[];
  ticker: string[];
};

let draft: Draft;
let onSaveCallback: ((cfg: Partial<LocationConfig>) => void) | null = null;
let onResetCallback: (() => void) | null = null;

export function renderAdminPanel(host: HTMLElement): HTMLElement {
  const panel = document.createElement('aside');
  panel.className = 'panel';
  panel.id = 'panel';
  panel.innerHTML = `
    <header class="panel-header">
      <h2>Display Settings</h2>
      <button class="icon-btn" id="closePanel" aria-label="Close">×</button>
    </header>
    <div class="panel-body" id="panelBody"></div>
    <footer class="panel-footer">
      <button class="btn ghost" id="resetBtn">Reset to default</button>
      <button class="btn primary" id="saveBtn">Save changes</button>
    </footer>
  `;
  host.appendChild(panel);
  return panel;
}

export function bindAdminCallbacks(
  onSave: (cfg: Partial<LocationConfig>) => void,
  onReset: () => void
): void {
  onSaveCallback = onSave;
  onResetCallback = onReset;
  document.getElementById('saveBtn')!.addEventListener('click', () => {
    if (!draft.intervals.length) { toast('Add at least one interval'); return; }
    onSaveCallback?.({
      location: { name: draft.name, sub: draft.sub },
      theme: draft.theme,
      ticker: draft.ticker,
      schedule: { loop: draft.loop, intervals: draft.intervals },
      sound: draft.sound
    });
  });
  document.getElementById('resetBtn')!.addEventListener('click', () => {
    if (confirm('Reset all settings to default for this location?')) onResetCallback?.();
  });
}

export function openPanel(cfg: LocationConfig): void {
  draft = {
    name: cfg.location.name,
    sub: cfg.location.sub,
    theme: cfg.theme,
    sound: cfg.sound,
    loop: cfg.schedule.loop,
    intervals: cfg.schedule.intervals.map(i => ({ ...i })),
    ticker: [...cfg.ticker]
  };
  document.getElementById('panel')!.classList.add('open');
  renderForm();
}

export function closePanel(): void {
  document.getElementById('panel')!.classList.remove('open');
}

function renderForm(): void {
  const body = document.getElementById('panelBody')!;
  body.innerHTML = `
    ${section('Location', `
      ${field('Display name', `<input type="text" id="f-name" value="${attr(draft.name)}" />`)}
      ${field('Subtitle (address, phone, etc.)', `<input type="text" id="f-sub" value="${attr(draft.sub)}" />`)}
    `)}

    ${section('Color theme', `
      <div class="swatches">
        ${THEMES.map(t => `
          <button class="swatch ${draft.theme === t.name ? 'active' : ''}" data-theme="${t.name}" style="background:${t.bg}">
            <span>${t.label}</span>
          </button>`).join('')}
      </div>
    `)}

    ${section('Schedule', `
      <p class="sub">Set how long each session and break lasts. The timer will cycle through these continuously.</p>
      <div class="rows" id="intervalRows">
        ${draft.intervals.map((iv, idx) => intervalRow(iv, idx)).join('')}
      </div>
      <button class="btn ghost full" id="addInterval">+ Add interval</button>
      <label class="check">
        <input type="checkbox" id="f-loop" ${draft.loop ? 'checked' : ''} />
        Loop continuously
      </label>
    `)}

    ${section('Scrolling messages', `
      <p class="sub">Shown along the bottom of the display. Add seasonal greetings, hours, promotions, etc.</p>
      <div class="rows" id="tickerRows">
        ${draft.ticker.map((msg, idx) => tickerRow(msg, idx)).join('')}
      </div>
      <button class="btn ghost full" id="addTicker">+ Add message</button>
    `)}

    ${section('Sound', `
      <label class="check">
        <input type="checkbox" id="f-sound" ${draft.sound ? 'checked' : ''} />
        Play a chime at the end of each interval
      </label>
    `)}

    ${section('Keyboard shortcuts', `
      <p class="kbd-list">
        <span><kbd>Space</kbd> Start / Pause</span>
        <span><kbd>R</kbd> Reset</span>
        <span><kbd>N</kbd> Skip interval</span>
        <span><kbd>F</kbd> Fullscreen</span>
        <span><kbd>A</kbd> Open this panel</span>
      </p>
    `)}
  `;
  bindFormEvents();
}

function section(title: string, inner: string): string {
  return `<section class="form-section"><h3>${title}</h3>${inner}</section>`;
}
function field(label: string, control: string): string {
  return `<label class="field"><span>${label}</span>${control}</label>`;
}
function attr(s: string): string {
  return s.replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;');
}
function intervalRow(iv: Interval, idx: number): string {
  const min = Math.floor(iv.durationSec / 60);
  const sec = iv.durationSec % 60;
  return `
    <div class="interval-row" data-idx="${idx}">
      <button class="reorder" data-action="up" title="Move up" ${idx === 0 ? 'disabled' : ''}>▲</button>
      <button class="reorder" data-action="down" title="Move down" data-last="${idx === draft.intervals.length - 1 ? '1' : '0'}">▼</button>
      <input type="text" class="iv-label" value="${attr(iv.label)}" placeholder="Label (e.g. PLAY)" />
      <div class="time-input">
        <input type="number" class="iv-min" value="${min}" min="0" max="999" />
        <span>min</span>
        <input type="number" class="iv-sec" value="${sec}" min="0" max="59" />
        <span>sec</span>
      </div>
      <button class="icon-btn remove" data-action="remove" title="Remove">×</button>
    </div>
  `;
}
function tickerRow(msg: string, idx: number): string {
  return `
    <div class="ticker-row" data-idx="${idx}">
      <input type="text" class="tk-msg" value="${attr(msg)}" placeholder="e.g. Open daily 6am – 10pm" />
      <button class="icon-btn remove" data-action="remove" title="Remove">×</button>
    </div>
  `;
}

function bindFormEvents(): void {
  document.getElementById('f-name')!.addEventListener('input', e => draft.name = (e.target as HTMLInputElement).value);
  document.getElementById('f-sub')!.addEventListener('input',  e => draft.sub  = (e.target as HTMLInputElement).value);
  document.getElementById('f-loop')!.addEventListener('change', e => draft.loop  = (e.target as HTMLInputElement).checked);
  document.getElementById('f-sound')!.addEventListener('change', e => draft.sound = (e.target as HTMLInputElement).checked);

  document.querySelectorAll<HTMLButtonElement>('.swatch').forEach(btn => {
    btn.addEventListener('click', () => {
      draft.theme = btn.dataset.theme as ThemeName;
      document.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
      btn.classList.add('active');
      document.body.className = 'theme-' + draft.theme;
    });
  });

  document.getElementById('addInterval')!.addEventListener('click', () => {
    draft.intervals.push({ label: 'NEW INTERVAL', durationSec: 60 });
    renderForm();
  });
  document.getElementById('addTicker')!.addEventListener('click', () => {
    draft.ticker.push('');
    renderForm();
  });

  document.querySelectorAll<HTMLDivElement>('.interval-row').forEach(row => {
    const idx = parseInt(row.dataset.idx!);
    row.querySelector<HTMLInputElement>('.iv-label')!.addEventListener('input', e => {
      draft.intervals[idx].label = (e.target as HTMLInputElement).value;
    });
    const updateDuration = () => {
      const min = parseInt((row.querySelector('.iv-min') as HTMLInputElement).value) || 0;
      const sec = parseInt((row.querySelector('.iv-sec') as HTMLInputElement).value) || 0;
      draft.intervals[idx].durationSec = Math.max(1, min * 60 + sec);
    };
    row.querySelector('.iv-min')!.addEventListener('input', updateDuration);
    row.querySelector('.iv-sec')!.addEventListener('input', updateDuration);
    row.querySelectorAll<HTMLButtonElement>('.reorder, .remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        if (action === 'remove') draft.intervals.splice(idx, 1);
        else if (action === 'up' && idx > 0) {
          [draft.intervals[idx-1], draft.intervals[idx]] = [draft.intervals[idx], draft.intervals[idx-1]];
        } else if (action === 'down' && idx < draft.intervals.length - 1) {
          [draft.intervals[idx+1], draft.intervals[idx]] = [draft.intervals[idx], draft.intervals[idx+1]];
        }
        renderForm();
      });
    });
  });

  document.querySelectorAll<HTMLDivElement>('.ticker-row').forEach(row => {
    const idx = parseInt(row.dataset.idx!);
    row.querySelector<HTMLInputElement>('.tk-msg')!.addEventListener('input', e => {
      draft.ticker[idx] = (e.target as HTMLInputElement).value;
    });
    row.querySelector<HTMLButtonElement>('.remove')!.addEventListener('click', () => {
      draft.ticker.splice(idx, 1);
      renderForm();
    });
  });
}

function toast(msg: string): void {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 2400);
}
